import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Star, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ASSETS, API } from "@/lib/assets";

const Hero = ({ heroId = "hero" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleJoin = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email,
        source: "hero",
      });
      setJoined(true);
      toast.success(`You're #${res.data.total_signups} on the FinX waitlist!`);
    } catch (err) {
      toast.error("Couldn't sign you up. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id={heroId}
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Soft gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 -left-32 w-[520px] h-[520px] rounded-full brand-gradient-soft blur-3xl opacity-70" />
        <div className="absolute -top-10 right-0 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#FFEDD5] via-[#FCE7F3] to-[#EDE9FE] blur-3xl opacity-80" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <div
            data-testid="hero-tagline"
            className="inline-flex items-center gap-2 rounded-full border border-[#EDE9FE] bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#5B21B6]"
          >
            <Sparkles size={14} className="text-[#A855F7]" />
            Paper trading available now on iOS
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900"
          >
            The{" "}
            <span className="brand-gradient-text">Duolingo</span>
            <br />
            of <span className="brand-gradient-text">Trading.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl"
          >
            Meet <span className="font-bold text-slate-900">Fifi</span> — your
            quant cat advisor. Learn investing with risk-free simulation on iPhone.
            Level up your avatar with XP, and let AI agents guide every move.
          </p>

          {/* Primary CTA: App Store */}
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              data-testid="hero-appstore-link"
              href="https://apps.apple.com/us/app/finx-stocks/id6783580699"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:-translate-y-0.5 transition-transform"
              aria-label="Download FinX on the App Store"
            >
              <img
                src="/assets/brand/appstore-badge.svg"
                alt="Download on the App Store"
                className="h-14 md:h-16 w-auto"
              />
            </a>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-500">
              <Smartphone size={16} />
              Google Play — coming soon
            </div>
          </div>

          <div className="mt-4 text-sm text-slate-500 font-semibold">
            Risk-free simulation · No credit card · For learners of all ages
          </div>

          {/* Secondary: waitlist for live trading */}
          <div className="mt-12 pt-8 border-t border-slate-200 max-w-xl">
            <div data-testid="hero-live-trading-launch" className="text-xs font-bold uppercase tracking-[0.18em] text-[#5B21B6] mb-2">
              Live trading planned for November 2026
            </div>
            <p data-testid="hero-waitlist-description" className="text-sm text-slate-600 mb-4">
              Live trading is not yet available. Join the waitlist for launch updates and early access.
            </p>
            <form
              data-testid="hero-waitlist-form"
              onSubmit={handleJoin}
              className="flex flex-col sm:flex-row gap-3"
            >
              <div className="flex-1 relative">
                <Input
                  data-testid="hero-waitlist-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={joined}
                  className="h-12 rounded-full pl-5 pr-4 bg-white border-2 border-slate-200 focus-visible:border-[#A855F7] focus-visible:ring-0 text-sm"
                />
              </div>
              <Button
                data-testid="hero-waitlist-submit"
                type="submit"
                disabled={loading || joined}
                className="h-12 rounded-full px-6 font-bold text-white brand-gradient border-0 hover:-translate-y-0.5 transition-all disabled:opacity-70"
              >
                {joined ? "You're in!" : loading ? "Joining..." : "Notify Me"}
                {!joined && !loading && <ArrowRight size={16} className="ml-1.5" />}
              </Button>
            </form>
          </div>
        </div>

        {/* Right: Fifi mascot */}
        <div className="lg:col-span-5 relative">
          <div className="relative w-full aspect-square max-w-[520px] mx-auto">
            {/* Glow */}
            <div className="absolute inset-6 fifi-glow rounded-full" />

            {/* Floating gradient cards */}
            <div className="absolute top-6 -left-2 sm:left-2 rotate-[-6deg] z-20 animate-float">
              <div
                data-testid="hero-card-qqq"
                className="candy-gradient-blue text-white rounded-2xl px-5 py-3 shadow-xl shadow-indigo-500/30"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">QQQ</div>
                <div className="text-2xl font-display font-black leading-none">712.51</div>
                <div className="text-xs font-bold mt-1">+0.18%</div>
              </div>
            </div>

            <div
              className="absolute bottom-8 -right-2 sm:right-0 rotate-[5deg] z-20 animate-float"
              style={{ animationDelay: "1.2s" }}
            >
              <div
                data-testid="hero-card-spy"
                className="candy-gradient-purple text-white rounded-2xl px-5 py-3 shadow-xl shadow-pink-500/30"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">SPY</div>
                <div className="text-2xl font-display font-black leading-none">738.53</div>
                <div className="text-xs font-bold mt-1">+0.12%</div>
              </div>
            </div>

            <div
              className="absolute top-1/2 -right-4 sm:-right-2 -translate-y-1/2 rotate-[3deg] z-20 animate-float"
              style={{ animationDelay: "0.6s" }}
            >
              <div className="bg-white rounded-2xl px-4 py-2.5 shadow-lg border border-slate-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center">
                  <Star size={16} className="text-white fill-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">XP</div>
                  <div className="text-sm font-display font-black text-slate-900 leading-none">1,493</div>
                </div>
              </div>
            </div>

            {/* Sparkles */}
            <span className="absolute top-12 right-10 text-2xl animate-sparkle">✦</span>
            <span
              className="absolute bottom-20 left-6 text-xl animate-sparkle text-[#EC4899]"
              style={{ animationDelay: "0.8s" }}
            >
              ✦
            </span>
            <span
              className="absolute top-1/3 left-1/4 text-base animate-sparkle text-[#5B6CFF]"
              style={{ animationDelay: "1.5s" }}
            >
              ✦
            </span>

            {/* Fifi */}
            <img
              data-testid="hero-fifi-image"
              src={ASSETS.fifiHero}
              alt="Fifi the Quant Cat Advisor cheering"
              className="relative z-10 w-full h-full object-contain drop-shadow-[0_25px_45px_rgba(168,85,247,0.25)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
